/**
 * Minimal typed API client shared by both apps. Every call goes through the API Gateway
 * (clients never touch Firestore; SPEC §4). Errors carry the HTTP status and a safe message.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    return this.send<T>(method, path, body === undefined ? undefined : JSON.stringify(body), 'application/json');
  }

  /** POST a file as the raw request body (e.g. a picture for the public images bucket). */
  upload<T>(path: string, file: Blob) {
    return this.send<T>('POST', path, file, file.type || 'application/octet-stream');
  }

  private async send<T>(method: string, path: string, body: BodyInit | undefined, contentType: string): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = this.opts.getToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = contentType;
    const f = this.opts.fetchImpl ?? fetch;
    const res = await f(`${this.opts.baseUrl}${path}`, {
      method,
      headers,
      body,
      // Bearer tokens only, no cookies (ADR-016). 'include' would need Access-Control-Allow-
      // Credentials on every response, and the browser reports its absence as a CORS error.
      credentials: 'omit',
    });
    if (res.status === 401) this.opts.onUnauthorized?.();
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = (data ?? {}) as { message?: string; code?: string };
      throw new ApiError(res.status, err.message ?? 'Something went sideways. Try again?', err.code);
    }
    return data as T;
  }

  get<T>(path: string) {
    return this.request<T>('GET', path);
  }
  post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, body ?? {});
  }
  put<T>(path: string, body: unknown) {
    return this.request<T>('PUT', path, body);
  }
}
